/**
 * "Your household": the family checks the parts of what the school has on file
 * that the step lists, asks for changes to any of them, and confirms with
 * "Everything here is right". A change is a request the office reviews, so a
 * family with one waiting can't send another until it's reviewed. A finished
 * step, or the office viewing as the family, shows the household with
 * nothing to do.
 */
import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { readFragment } from 'gql.tada';
import type { AppLayoutContext } from '~/types';
import { Button } from '~/components/ui/button';
import { useViewAsFamily } from '~/hooks/useViewAsFamily';
import { useMyFamilyQuery } from '~/pages/MyFamily/useMyFamilyQuery';
import { ParentFamilyInfo, type HouseholdSection } from '~/pages/MyFamily/ParentFamilyInfo';
import { HouseholdChangeRequestFragment } from '~/pages/ParentHousehold/HouseholdChangeRequestFragment';
import { PendingChangesBanner } from '~/pages/ParentHousehold/PendingChangesBanner';
import { EditHouseholdDialog } from '~/pages/ParentHousehold/EditHouseholdDialog';
import { useEnrollYear } from '../enrollYear';
import { StepFooter, StepHeader, type StepProps } from './stepParts';
import { useStepAnswers } from './useStepAnswers';

/** The household parts a step lists, and the card of My Family that shows each. */
const SECTION_FOR_FIELD: Record<string, HouseholdSection> = {
  address: 'address',
  guardians: 'guardians',
  emergencyContacts: 'emergency',
  authorizedPickup: 'pickup',
  churchAffiliation: 'church',
};

export default function HouseholdStep({ step, readOnly, overviewPath, stepPath }: StepProps) {
  const { school } = useOutletContext<AppLayoutContext>();
  const enrollYear = useEnrollYear();
  const { familyId: viewAsFamilyId } = useViewAsFamily(school?.slug);
  const { data, loading, error, refetch } = useMyFamilyQuery(
    school?.id,
    enrollYear.status === 'ready' ? enrollYear.year.id : undefined,
    viewAsFamilyId ?? undefined
  );
  const [editing, setEditing] = useState<HouseholdSection | null>(null);
  const { submit, saveState, errors, submitting } = useStepAnswers<Record<string, never>>({
    itemId: step.item.id,
    initial: {},
    overviewPath,
    stepPath,
  });

  const family = data?.myFamily;
  const sections = (step.household?.fields ?? []).flatMap((field) => SECTION_FOR_FIELD[field] ?? []);
  const pending = readFragment(HouseholdChangeRequestFragment, family?.pendingHouseholdChangeRequest);
  const lastReviewed = readFragment(HouseholdChangeRequestFragment, family?.lastReviewedHouseholdChangeRequest);

  return (
    <div className="space-y-6">
      <StepHeader step={step} />

      {family ? (
        <>
          <PendingChangesBanner pending={pending} lastReviewed={lastReviewed} />
          <ParentFamilyInfo
            family={family}
            sections={sections}
            onEditSection={readOnly || pending ? undefined : setEditing}
          />
        </>
      ) : loading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : error ? (
        <div className="space-y-3">
          <p className="text-destructive">We couldn't load your household.</p>
          <Button type="button" variant="outline" className="min-h-11 w-full md:w-auto" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      ) : null}

      {readOnly ? null : (
        <StepFooter
          errors={errors}
          saveState={saveState}
          submitting={submitting}
          label="Everything here is right"
          onSubmit={submit}
          disabled={!family}
        />
      )}

      {family && editing ? (
        <EditHouseholdDialog
          section={editing}
          open
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
          family={family}
          onSubmitted={() => void refetch()}
        />
      ) : null}
    </div>
  );
}
